import { SettingsManager } from "@viamap/viamap2-common";
import { FilterSpec, FilterValue } from "../states/ExploreSearchState";
import { ESInterfaceBase } from "./ESInterfaceBase.ts";
import AWS from "aws-sdk";

export type { UTMGridData, UTMGridItem } from "./ESInterfaceBase.ts";

/**
 * Interface to Elastic Search
 */

export class ESInterfaceJWTAuth extends ESInterfaceBase {
   private jwtIdToken: string | null = null;
   private noSQLDataUrl: string = "";
   private static refreshTokenPromise: Promise<string | undefined> | null = null;

   constructor(index: string, userSession?: any) {
      super(index);
      this.noSQLDataUrl = SettingsManager.getSystemSetting("getNoSQLDataUrl");

      this.jwtIdToken = userSession?.idToken?.getJwtToken();
   }

   private getAuthStorageContext(): {
      authData: { ClientId: string; Region?: string };
      idTokenKey: string;
      accessTokenKey: string;
      refreshTokenKey: string;
   } | undefined {
      try {
         const authData = SettingsManager.getSystemSetting("authData") as { ClientId?: string; Region?: string } | undefined;
         if (!authData || !authData.ClientId) {
            return undefined;
         }

         const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
         const username = localStorage.getItem(lastAuthUserKey);

         if (!username) {
            return undefined;
         }

         return {
            authData: { ClientId: authData.ClientId, Region: authData.Region },
            idTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`,
            accessTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.accessToken`,
            refreshTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.refreshToken`
         };
      } catch (error) {
         console.warn("Failed to read auth storage context:", error);
         return undefined;
      }
   }

   private getJwtTokenFromStorage(): string | undefined {
      const context = this.getAuthStorageContext();
      if (!context) return undefined;
      try {
         return localStorage.getItem(context.idTokenKey) || undefined;
      } catch (error) {
         console.warn("Failed to get JWT token from localStorage:", error);
         return undefined;
      }
   }

   private decodeJwtPayload(token: string): { exp?: number } | undefined {
      try {
         const payload = token.split(".")[1];
         if (!payload) return undefined;
         const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
         const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - normalizedPayload.length % 4) % 4), "=");
         const decoded = JSON.parse(atob(paddedPayload));
         return decoded;
      } catch {
         return undefined;
      }
   }

   private isTokenExpired(token: string, skewMs: number = 30 * 1000): boolean {
      const payload = this.decodeJwtPayload(token);
      if (!payload?.exp) {
         return false;
      }
      const expiresAtMs = payload.exp * 1000;
      return Date.now() >= (expiresAtMs - skewMs);
   }

   private async refreshJwtToken(): Promise<string | undefined> {
      const context = this.getAuthStorageContext();
      if (!context?.authData.Region) {
         return undefined;
      }

      let refreshToken: string | null = null;
      try {
         refreshToken = localStorage.getItem(context.refreshTokenKey);
      } catch {
         refreshToken = null;
      }
      if (!refreshToken) {
         return undefined;
      }

      if (!ESInterfaceJWTAuth.refreshTokenPromise) {
         ESInterfaceJWTAuth.refreshTokenPromise = new Promise((resolve) => {
            const cognito = new AWS.CognitoIdentityServiceProvider({ region: context.authData.Region });
            cognito.initiateAuth(
               {
                  AuthFlow: "REFRESH_TOKEN_AUTH",
                  ClientId: context.authData.ClientId,
                  AuthParameters: {
                     REFRESH_TOKEN: refreshToken || ""
                  }
               } as AWS.CognitoIdentityServiceProvider.InitiateAuthRequest,
               (error, result) => {
                  if (error) {
                     console.warn("Failed to refresh JWT token:", error);
                     resolve(undefined);
                     return;
                  }

                  const idToken = result?.AuthenticationResult?.IdToken;
                  const accessToken = result?.AuthenticationResult?.AccessToken;
                  if (!idToken) {
                     resolve(undefined);
                     return;
                  }

                  try {
                     localStorage.setItem(context.idTokenKey, idToken);
                     if (accessToken) {
                        localStorage.setItem(context.accessTokenKey, accessToken);
                     }
                  } catch {
                     // Ignore storage write errors and still use the refreshed token in-memory.
                  }
                  resolve(idToken);
               }
            );
         });
      }

      try {
         const refreshedToken = await ESInterfaceJWTAuth.refreshTokenPromise;
         if (refreshedToken) {
            this.jwtIdToken = refreshedToken;
         }
         return refreshedToken;
      } finally {
         ESInterfaceJWTAuth.refreshTokenPromise = null;
      }
   }

   private async getJwtToken(forceRefresh: boolean = false): Promise<string | undefined> {
      const fromStorage = this.getJwtTokenFromStorage();
      const token = this.jwtIdToken || fromStorage;
      if (token && !forceRefresh && !this.isTokenExpired(token)) {
         this.jwtIdToken = token;
         return token;
      }

      const refreshedToken = await this.refreshJwtToken();
      if (refreshedToken) {
         return refreshedToken;
      }

      if (token) {
         this.jwtIdToken = token;
      }
      return token || undefined;
   }

   private async postNoSql(requestBody: any, jwtToken?: string): Promise<Response> {
      return fetch(this.noSQLDataUrl, {
         method: "POST",
         headers: {
            ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` }),
            "content-type": "application/json"
         },
         body: JSON.stringify(requestBody)
      });
   }

   private async postNoSqlWithRefreshRetry(requestBody: any): Promise<Response> {
      const token = await this.getJwtToken();
      let response = await this.postNoSql(requestBody, token);

      if (response.status === 401 || response.status === 403) {
         const refreshedToken = await this.getJwtToken(true);
         response = await this.postNoSql(requestBody, refreshedToken);
      }
      return response;
   }

   protected async executeQuery(urlCommand: string, query: any): Promise<any> {
      const requestBody = {
         index: "/" + urlCommand,
         query: query
      }
      return this.executeNoSqlRequest(requestBody);
   }

   private async executeNoSqlRequest(requestBody: any): Promise<any> {
      const response = await this.postNoSqlWithRefreshRetry(requestBody);
      if (response.status !== 200) {
         const errorText = await response.text().catch(() => response.statusText);
         throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      return response.json();
   }

   async doQuery(query: any, pageSize?: number): Promise<any> {
      if (pageSize !== undefined) {
         return this.doQueryWithPagination(query, pageSize);
      }
      return super.doQuery(query);
   }

   private async doQueryWithPagination(query: any, pageSize: number): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after
      const queryWithSort = query.sort
         ? { ...query }
         : { ...query, sort: [{ _id: 'asc' }] };

      while (hasMore && currentPage < maxPages) {
         currentPage++;

         try {
            const esQuery: any = {
               ...queryWithSort,
               size: pageSize
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            const requestBody = {
               index: "/" + this.indexName + "/_search",
               query: esQuery,
               maxRows: pageSize,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const body = await this.executeNoSqlRequest(requestBody);
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore;
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === pageSize;
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._id) {
                     searchAfter = [lastHit._id];
                  } else {
                     hasMore = false;
                  }
               }
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      // Return in same format as non-paginated query
      return {
         hits: {
            total: { value: total || allHits.length },
            hits: allHits
         }
      };
   }


   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count: number,
      includeTotalAndReturned: true,
      pageSize?: number): Promise<{ data: any, total: number, returned: number }>
   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count?: number,
      includeTotalAndReturned?: false,
      pageSize?: number): Promise<any[]>
   async doSearch(selectableFilterList: { [id: string]: FilterSpec },
      activeFilters: { [id: string]: FilterValue },
      count?: number,
      includeTotalAndReturned?: boolean,
      pageSize?: number): Promise<any> {
      if (pageSize !== undefined) {
         let query = this.makeQuery(selectableFilterList, activeFilters);
         if (count) {
            query = { ...query, "size": count, "version": true };
         }
         query = { ...this.runtimeMapping, ...query };
         return this.doSearchWithPagination(query, pageSize, includeTotalAndReturned, count);
      }
      return super.doSearch(selectableFilterList, activeFilters, count as any, includeTotalAndReturned as any);
   }

   private async doSearchWithPagination(query: any, pageSize: number, includeTotalAndReturned?: boolean, requestedSize?: number): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after (use _id as default if no sort specified)
      const queryWithSort = query.sort
         ? { ...query }
         : { ...query, sort: [{ _id: 'asc' }] };

      while (hasMore && currentPage < maxPages && (!requestedSize || allHits.length < requestedSize)) {
         currentPage++;

         try {
            const currentPageSize = requestedSize
               ? Math.min(pageSize, requestedSize - allHits.length)
               : pageSize;
            const esQuery: any = {
               ...queryWithSort,
               size: currentPageSize
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            const requestBody = {
               index: "/" + this.indexName + "/_search",
               query: esQuery,
               maxRows: currentPageSize,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const body = await this.executeNoSqlRequest(requestBody);
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore && (!requestedSize || allHits.length < requestedSize);
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === currentPageSize && (!requestedSize || allHits.length < requestedSize);
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._id) {
                     searchAfter = [lastHit._id];
                  } else {
                     hasMore = false;
                  }
               }
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      // Transform hits to match expected format
      const result = allHits.map((hit: any) => { return { _version: hit._version, ...hit._source } });

      if (includeTotalAndReturned) {
         return { data: result, total: total || result.length, returned: result.length };
      }
      return result;
   }

   async runCommand(method: string, urlCommand: string, body: any, noJSON = false): Promise<any> {
      const requestBody: any = {
         index: "/" + urlCommand,
         method: method
      };

      if (body !== undefined) {
         if (noJSON) {
            // For bulk operations, body is already a string - send as-is
            requestBody.body = body;
         } else {
            // For document operations, stringify the body so lambda can handle it
            requestBody.body = typeof body === 'string' ? body : JSON.stringify(body);
         }
      }

      try {
         const response = await this.postNoSqlWithRefreshRetry(requestBody);
         if (response.status != 200) {
            const errorText = await response.text().catch(() => response.statusText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
         }
         let result = await response.json();
         if (result.error) {
            throw new Error(result.error.reason || result.error);
         }
         return result;
      } catch (err: any) {
         console.error("Got error in runCommand: ", urlCommand, err);
         throw new Error(err.message || err);
      }
   }
}